import React, { useRef, useState, useEffect } from 'react';
import { brandContent } from '../data/mock';
import topImage from '../assets/female-in-traditional-wear-silver.png';
import bottomImage from '../assets/female-in-daily-wear-silver.png';

/**
 * CrossFadeSection — full-bleed sticky scroll experience.
 *
 * No heading. Dark navy background. Images fill the full left half edge-to-edge.
 * The images have an intentionally empty right portion — text lives there, large.
 *
 * Progress timeline (0 → 1 for sticky portion):
 *   ENTRANCE   Section fades in (opacity 0 -> 1) as it hits the 30% viewport mark
 *   PRE-STICKY  Panel 1 fades in when section top covers 35% of viewport
 *   0.10 – 0.25  Panel 1 slides up and fades OUT
 *   0.20 – 0.40  Image cross-fade (top → bottom)
 *   0.20 – 0.50  Panel 2 fade in (slower entrance)
 *   EXIT       Section fades out (opacity 1 -> 0) at the very end
 *
 * Mobile: image fills top 50%, text panels below with same slide-up/out behaviour.
 */

const MULTIPLIER = 2.2;

function lerp(progress, start, end) {
  return Math.min(1, Math.max(0, (progress - start) / (end - start)));
}

const EMPHASIZED_WORDS = ['CELEBRATE LIFE', 'CHERISH', 'HARMONY', 'WEALTH', 'GRACEFUL', 'LIFESTYLE'];

function renderWithEmphasis(text) {
  let parts = [text];
  EMPHASIZED_WORDS.forEach(word => {
    parts = parts.flatMap(part => {
      if (typeof part !== 'string') return [part];
      const segments = part.split(word);
      return segments.flatMap((seg, i) =>
        i < segments.length - 1
          ? [seg, <strong key={`${word}-${i}`} className="font-semibold text-brand-gradient-gold-end">{word}</strong>]
          : [seg]
      );
    });
  });
  return parts;
}

const CrossFadeSection = () => {
  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [entranceProgress, setEntranceProgress] = useState(0);
  const [exitProgress, setExitProgress] = useState(1);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) { setProgress(0.8); return; }

    let rafHandle = null;
    const onScroll = () => {
      if (rafHandle) return;
      rafHandle = requestAnimationFrame(() => {
        rafHandle = null;
        if (!containerRef.current) return;
        const { top, height, bottom } = containerRef.current.getBoundingClientRect();
        const vh = window.innerHeight;

        // Entrance: 0 at start, 1 when section covers 30% of viewport
        const entrance = Math.min(1, Math.max(0, (vh - top) / vh));
        setEntranceProgress(entrance);

        // Exit: 1 when section is active, 0 when section bottom leaves viewport
        const exit = Math.min(1, Math.max(0, bottom / vh));
        setExitProgress(exit);

        const scrollable = height - vh;
        const scrolled = Math.max(0, -top);
        setProgress(Math.min(1, scrolled / scrollable));
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafHandle) cancelAnimationFrame(rafHandle);
    };
  }, []);

  const { whatWeAre, whyWeExist } = brandContent.brandThesis;

  // Image cross-fade & Panel 2 Entrance
  const topImageOpacity = 1 - lerp(progress, 0.45, 0.70);

  // Overall image container fade-in during entrance to prevent clashing with Hero image
  const baseImageOpacity = lerp(entranceProgress, 0.4, 0.9);

  // Panel 1 logic:
  // Starts fading in during entrance (35% visibility) and fades out during sticky scroll
  const p1In = lerp(entranceProgress, 0.35, 0.60);
  const p1Out = lerp(progress, 0.25, 0.45);
  const panel1Opacity = p1In * (1 - p1Out);
  const panel1Y = (1 - p1In) * 40 - p1Out * 40;

  // Panel 2: reveals gradually alongside cross-fade, ends with a bit of reading room
  const p2In = lerp(progress, 0.50, 0.80);
  const panel2Opacity = p2In;
  const panel2Y = (1 - p2In) * 60;

  // Overall section fade for natural entry/exit
  const sectionOpacity = lerp(entranceProgress, 0.1, 0.3) * lerp(exitProgress, 0.1, 0.3);

  const panelStyle = (opacity, translateY) => ({
    opacity,
    transform: `translateY(${translateY}px)`,
    willChange: 'transform, opacity',
    pointerEvents: opacity < 0.05 ? 'none' : 'auto',
  });

  return (
    <div
      ref={containerRef}
      id="cross-fade-section"
      style={{ height: `${MULTIPLIER * 100}vh` }}
      className="relative"
    >
      {/* Sticky full-viewport panel — smooth scroll-driven opacity for natural ingress/egress */}
      <div 
        className="sticky top-0 h-screen overflow-hidden bg-primary-navy"
        style={{ opacity: sectionOpacity, willChange: 'opacity' }}
      >

        {/* ── Desktop / Tablet: side-by-side ── */}
        <div className="hidden md:flex h-full">

          {/* Left: full-bleed image stack — fills entire left half */}
          <div className="w-1/2 relative" style={{ opacity: baseImageOpacity, willChange: 'opacity' }}>
            <img
              src={bottomImage}
              alt="Woman in daily silver jewelry — ZURIAN everyday wear"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: '10% top' }}
            />
            <img
              src={topImage}
              alt="Woman in traditional silver jewelry — ZURIAN heritage wear"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: '10% top', opacity: topImageOpacity, willChange: 'opacity' }}
            />
            {/* Subtle right-edge vignette to blend into text panel */}
            <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-r from-transparent to-primary-navy pointer-events-none" />
          </div>

          {/* Right: text panels — large type to fill the empty image space */}
          <div className="w-1/2 relative flex items-center bg-primary-navy">

            {/* Panel 1 — What We Are */}
            <div
              className="absolute inset-0 flex flex-col justify-center px-10 xl:px-14 2xl:px-20"
              style={panelStyle(panel1Opacity, panel1Y)}
            >
              <p className="font-heading text-[11px] tracking-[0.3em] uppercase text-primary-beige/40 mb-6">
                What We Are
              </p>
              <p className="font-body text-3xl xl:text-4xl 2xl:text-5xl leading-[1.15] text-primary-beige mb-5">
                {whatWeAre.paragraph1}
              </p>
              <p className="font-body text-sm xl:text-base leading-relaxed text-primary-beige/60 max-w-xs xl:max-w-sm mb-3">
                {whatWeAre.paragraph2}
              </p>
              <p className="font-body text-sm xl:text-base leading-relaxed text-primary-beige/60 max-w-xs xl:max-w-sm">
                {renderWithEmphasis(whatWeAre.paragraph2b)}
              </p>
            </div>

            {/* Panel 2 — Why We Exist */}
            <div
              className="absolute inset-0 flex flex-col justify-center px-10 xl:px-14 2xl:px-20"
              style={panelStyle(panel2Opacity, panel2Y)}
            >
              <p className="font-heading text-[11px] tracking-[0.3em] uppercase text-primary-beige/40 mb-6">
                Why We Exist
              </p>
              <p className="font-body text-3xl xl:text-4xl 2xl:text-5xl leading-[1.15] text-primary-beige mb-5">
                {whyWeExist.paragraph1}
              </p>
              <p className="font-body text-sm xl:text-base leading-relaxed text-primary-beige/60 max-w-xs xl:max-w-sm mb-3">
                {whyWeExist.paragraph2}
              </p>
              <p className="font-body text-sm xl:text-base leading-relaxed text-primary-beige/60 max-w-xs xl:max-w-sm mb-3">
                {renderWithEmphasis(whyWeExist.paragraph2b)}
              </p>
              <p className="font-body text-sm xl:text-base leading-relaxed text-primary-beige/60 max-w-xs xl:max-w-sm">
                {renderWithEmphasis(whyWeExist.paragraph2c)}
              </p>
            </div>
          </div>
        </div>

        {/* ── Mobile: stacked ── */}
        <div className="flex md:hidden flex-col h-full">

          {/* Image — top 50% of screen */}
          <div className="relative w-full" style={{ height: '50vh', opacity: baseImageOpacity, willChange: 'opacity' }}>
            <img
              src={bottomImage}
              alt="Woman in daily silver jewelry — ZURIAN everyday wear"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: '10% top' }}
            />
            <img
              src={topImage}
              alt="Woman in traditional silver jewelry — ZURIAN heritage wear"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: '10% top', opacity: topImageOpacity, willChange: 'opacity' }}
            />
            {/* Bottom vignette */}
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-primary-navy to-transparent pointer-events-none" />
          </div>

          {/* Text — bottom 50% */}
          <div className="relative flex-1 overflow-hidden bg-primary-navy">

            {/* Panel 1 */}
            <div
              className="absolute inset-0 flex flex-col justify-center px-6 sm:px-8 overflow-y-auto"
              style={panelStyle(panel1Opacity, panel1Y)}
            >
              <p className="font-heading text-[10px] tracking-[0.3em] uppercase text-primary-beige/40 mb-3">
                What We Are
              </p>
              <p className="font-body text-xl sm:text-2xl leading-snug text-primary-beige mb-3">
                {whatWeAre.paragraph1}
              </p>
              <p className="font-body text-xs sm:text-sm leading-relaxed text-primary-beige/60 mb-2">
                {whatWeAre.paragraph2}
              </p>
              <p className="font-body text-xs sm:text-sm leading-relaxed text-primary-beige/60">
                {renderWithEmphasis(whatWeAre.paragraph2b)}
              </p>
            </div>

            {/* Panel 2 */}
            <div
              className="absolute inset-0 flex flex-col justify-center px-6 sm:px-8 overflow-y-auto"
              style={panelStyle(panel2Opacity, panel2Y)}
            >
              <p className="font-heading text-[10px] tracking-[0.3em] uppercase text-primary-beige/40 mb-3">
                Why We Exist
              </p>
              <p className="font-body text-xl sm:text-2xl leading-snug text-primary-beige mb-3">
                {whyWeExist.paragraph1}
              </p>
              <p className="font-body text-xs sm:text-sm leading-relaxed text-primary-beige/60 mb-2">
                {whyWeExist.paragraph2}
              </p>
              <p className="font-body text-xs sm:text-sm leading-relaxed text-primary-beige/60 mb-2">
                {renderWithEmphasis(whyWeExist.paragraph2b)}
              </p>
              <p className="font-body text-xs sm:text-sm leading-relaxed text-primary-beige/60">
                {renderWithEmphasis(whyWeExist.paragraph2c)}
              </p>
            </div>
          </div>
        </div>

        {/* Scroll hint — fades out quickly */}
        <div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none z-20"
          style={{ opacity: Math.max(0, 1 - progress * 12) }}
        >
          <p className="font-heading text-[9px] tracking-[0.3em] uppercase text-primary-beige/25">Scroll</p>
          <div className="w-px h-6 bg-primary-beige/15 animate-pulse" />
        </div>

      </div>
    </div>
  );
};

export default CrossFadeSection;
